/**
 * <ConsolidationMath /> — "Vantage + Datadog + CloudZero vs CARTIE"
 *
 * v34.68 (Jun 3 2026) — Nova's pricing-visibility unlock (round 3):
 * "Lead with consolidation math, not a price tag. If you replace $5,000
 *  worth of tools with $1,997, surface THAT not the price."
 *
 * Replaces "you're paying $1,997/mo" with "you're saving $36,036/year on
 * tools alone — before any actual cloud savings."
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, X, Check, TrendingDown } from 'lucide-react';

const REPLACED_STACK = [
  { tool: 'Vantage', plan: 'Team', price: 1500, role: 'Multi-cloud cost dashboard' },
  { tool: 'Datadog', plan: 'Cost Mgmt + LLM Observability', price: 1800, role: 'Observability + LLM tracing' },
  { tool: 'CloudZero', plan: 'Pro', price: 1200, role: 'Unit economics + COGS' },
  { tool: 'Helicone', plan: 'Pro', price: 500, role: 'LLM caching + token logs' },
];

const CARTIE_PRICE = 1997;
const TOTAL_REPLACED = REPLACED_STACK.reduce((s, t) => s + t.price, 0);
const MONTHLY_SAVINGS = TOTAL_REPLACED - CARTIE_PRICE;
const YEARLY_SAVINGS = MONTHLY_SAVINGS * 12;

const ConsolidationMath = () => (
  <section
    className="relative py-16 px-4 bg-slate-950 border-y border-emerald-500/10"
    data-testid="consolidation-math-section"
  >
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute left-1/2 top-1/3 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-emerald-500/[0.05] blur-[120px]" />
    </div>

    <div className="max-w-6xl mx-auto relative">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-300 mb-4">
          <TrendingDown className="w-3 h-3" /> Consolidation math · before any cloud savings
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight text-white mb-3">
          Pay <span className="text-emerald-400">$1,997</span> for CARTIE. Cancel <span className="line-through text-rose-400">${TOTAL_REPLACED.toLocaleString()}</span> in other tools.
        </h2>
        <p className="text-sm text-slate-400 max-w-2xl mx-auto leading-relaxed">
          You save <span className="text-emerald-300 font-semibold">${MONTHLY_SAVINGS.toLocaleString()}/mo</span> on subscriptions alone — <span className="text-emerald-300 font-semibold">${YEARLY_SAVINGS.toLocaleString()}/year</span> — before CARTIE finds a single dollar of cloud waste.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* LEFT — What you cancel */}
        <div className="rounded-2xl border border-rose-500/30 bg-slate-900 p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <X className="w-5 h-5 text-rose-400" />
              What you cancel
            </h3>
            <span className="text-xs font-mono text-rose-300">today's stack</span>
          </div>
          <ul className="space-y-2.5 mb-5">
            {REPLACED_STACK.map((t, i) => (
              <li
                key={i}
                className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-slate-950/60 border border-white/5"
                data-testid={`consolidation-replaced-${i}`}
              >
                <div>
                  <p className="text-sm font-semibold text-white">{t.tool}<span className="text-slate-500 font-normal ml-2 text-xs">{t.plan}</span></p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{t.role}</p>
                </div>
                <span className="text-sm font-mono text-rose-300">${t.price.toLocaleString()}/mo</span>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between pt-4 border-t border-white/5">
            <span className="text-xs text-slate-400 uppercase tracking-wider font-mono">Total killed</span>
            <span className="text-2xl font-bold text-rose-300">${TOTAL_REPLACED.toLocaleString()}<span className="text-xs text-slate-500 font-normal ml-1">/mo</span></span>
          </div>
        </div>

        {/* RIGHT — What you pay */}
        <div className="rounded-2xl border border-emerald-500/40 bg-slate-900 p-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 px-3 py-1 bg-emerald-500 text-slate-950 text-[10px] font-bold tracking-wider rounded-bl-xl">
            REPLACES ALL 4
          </div>
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Check className="w-5 h-5 text-emerald-400" />
              What you pay
            </h3>
            <span className="text-xs font-mono text-emerald-300">CARTIEAI · Enterprise</span>
          </div>

          <div className="bg-slate-950/60 border border-emerald-500/20 rounded-xl p-5 mb-5">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-5xl font-bold text-white">${CARTIE_PRICE.toLocaleString()}</span>
              <span className="text-sm text-slate-400">/mo</span>
            </div>
            <p className="text-xs text-slate-400">26 engines · unlimited seats · 3× ROI pledge</p>
          </div>

          <div className="space-y-2 mb-5">
            {[
              'Cost dashboards · everything Vantage does',
              'LLM observability · everything Datadog AI does',
              'Unit economics · everything CloudZero does',
              'Token caching · everything Helicone does',
              'Plus: Auto-PR fixes · Slack bot · CLI · MCP server',
            ].map((line, i) => (
              <div key={i} className="flex items-start gap-2 text-[13px] text-slate-300">
                <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <span>{line}</span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-emerald-500/15">
            <span className="text-xs text-slate-400 uppercase tracking-wider font-mono">You save</span>
            <span className="text-2xl font-bold text-emerald-300">${MONTHLY_SAVINGS.toLocaleString()}<span className="text-xs text-slate-500 font-normal ml-1">/mo · ${(YEARLY_SAVINGS / 1000).toFixed(0)}K/yr</span></span>
          </div>
        </div>
      </div>

      <div className="mt-8 text-center">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm px-6 py-3 rounded-full transition-all hover:-translate-y-0.5 shadow-[0_10px_30px_-8px_rgba(16,185,129,0.6)]"
          data-testid="consolidation-cta-pricing"
        >
          See the full pricing breakdown
          <ArrowRight className="w-4 h-4" />
        </Link>
        <p className="text-[11px] text-slate-500 mt-3 max-w-md mx-auto">
          Stack prices are public Q2 2026 list rates. Your actual savings may be higher with negotiated discounts.
        </p>
      </div>
    </div>
  </section>
);

export default ConsolidationMath;
